/**
 * Runtime deployment-environment detection, keyed off the browser hostname.
 *
 * The frontend is served from the same origin as its API (`environment.ts` uses
 * a relative `/api`), so there is no build-time prod/qa split — the environment
 * has to be derived at runtime from `window.location.hostname`.
 *
 * Edit {@link ENV_HOSTNAMES} when a deployment host changes; it is the single
 * source of truth. Unknown hosts resolve to `'other'` and are surfaced verbatim
 * (never mislabelled as prod/qa).
 */
export type AppEnvironment = 'prod' | 'qa' | 'local' | 'other';

/** Known deployment hostnames → environment. The one place to edit for new hosts. */
export const ENV_HOSTNAMES: Readonly<Record<string, AppEnvironment>> = {
  'cve.anessa.com': 'prod',
  'cveqa.anessa.com': 'qa',
};

/** Loopback / dev hostnames that count as a local environment. */
const LOCAL_HOSTNAMES: ReadonlySet<string> = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '[::1]',
  '::1',
]);

/** Classify a hostname into a deployment environment. */
export function resolveEnvironment(hostname: string): AppEnvironment {
  const host = (hostname ?? '').trim().toLowerCase();
  const mapped = ENV_HOSTNAMES[host];
  if (mapped) return mapped;
  if (LOCAL_HOSTNAMES.has(host) || host.endsWith('.local')) return 'local';
  return 'other';
}
